import {
  Component,
  OnInit,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges, OnDestroy, Output, EventEmitter, AfterViewInit
} from '@angular/core';
import * as c3 from 'c3';
import { GraphUtilsService } from '../../services/graph-utils.service';
import { DataService } from '../../services/data.service';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-c3-time-series',
    templateUrl: './c3-time-series.component.html',
    styleUrls: ['./c3-time-series.component.scss'],
    standalone: false
})
export class C3TimeSeriesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() data: any;
  @Input() kpi: any;
  @Input() paramName: any;
  @Input() rule1Enabled: boolean;
  @Input() rule6Enabled: boolean;
  @Input() rule7Enabled: boolean;
  @Input() outliers: boolean;
  @Input() uslLsl: boolean;
  @Input() redZone: boolean;
  @Input() yzoom: any;
  @Input() inclindXaxis = 0;
  @Input() from: string = '';

  @Output() pointSelection: EventEmitter<object> = new EventEmitter();

  chart: any = null;

  violatedIndexes: any = {
    rule1: [],
    rule6: [],
    rule7: [],
  };

  thresholds: any = {
    usl: null,
    lsl: null,
    ucl: null,
    lcl: null,
    avg: null,
    avgPlus1Sigma: null,
    avgMinus1Sigma: null,
    avgPlus2Sigma: null,
    avgMinus2Sigma: null,
    redZone: null
  };

  colors: any = {};
  subscription: any;
  selectedPoints: any[] = [];

  constructor(
    private elRef: ElementRef,
    private graphUtil: GraphUtilsService,
    private dataSer: DataService,
    private datePipe: DatePipe
  ) {

    this.subscription = dataSer.events.subscribe(this.replotGraphEventSubscription);
  }

  replotGraphEventSubscription = (res: any) => {
    if (res.name === 'replotGraph') {
      let timeoutval = 0;
      if (res.args && res.args[0] === 'menuToggle') {
        timeoutval = 101;
      }
      setTimeout(() => {
        this.checkAndReplotGraph();
      }, timeoutval);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    //  changed to fix memory leak
    if (this.chart) {
      this.chart.destroy();
    }
    this.checkAndReplotGraph();
  }

  ngOnInit() {
    this.colors = this.graphUtil.getColorList();
  }

  ngAfterViewInit(): void {
    this.checkAndReplotGraph();
  }

  checkAndReplotGraph() {
    if (this.data && this.data.length && this.kpi.avg !== null) {
      this.thresholds = this.graphUtil.setThreshHolds(this.kpi, this.outliers, this.uslLsl);
      this.violatedIndexes = this.graphUtil.populateViolatedIndexes(this.data);
      this.generateChart(this.data);
      this.selectedPoints = [];
    } else {
      this.chart = null;
    }
  }

  generateChart = (data) => {
    const bounds = this.graphUtil.findBoundaryVals(this.data, 'value', this.thresholds);

    this.chart = c3.generate({
      bindto: this.elRef.nativeElement.children[0].children[0],
      legend: { show: false },
      padding: {
        right: 25,
        top: 10
      },
      data: {
        x: 'time',
        columns: [
          ['time', ...this.data.map(elem => elem.convertedTS)],
          [this.paramName, ...this.data.map(elem => elem.value)],
        ],
        type: 'line',
        color: (d: any, i: c3.DataPoint) => this.colorGenerator(d, i),
        selection: {
          enabled: !this.outliers,
          multiple: true
        },
        onselected: (d) => {
          this.onPointSelect(d);
        },
        onunselected: (d) => {
          this.onPointUnSelect(d);
        }
      },
      transition: {
        duration: 0
      },
      point: {
        r: 3
      },
      axis: {
        x: {
          type: 'timeseries',
          tick: {
            count: this.data && this.data.length ? (this.data.length < 10 ? 10 : this.data.length) : 10,
            format: '%_d %b %y %H:%M',
            multiline: true,
            rotate: this.inclindXaxis,
          },
        },
        y: {
          max: this.yzoom.max ? this.yzoom.max : bounds.max,
          min: this.yzoom.min ? this.yzoom.min : bounds.min
          //  padding: { top: 5, }
        }
      },
      regions: [
        {
          axis: 'y',
          end: this.thresholds.ucl,
          start: this.thresholds.lcl,
          class: 'control region'
        },
        {
          axis: 'y',
          end: this.thresholds.avgPlus2Sigma,
          start: this.thresholds.avgMinus2Sigma,
          class: 'average-2-sigma region'
        },
        {
          axis: 'y',
          end: this.thresholds.avgPlus1Sigma,
          start: this.thresholds.avgMinus1Sigma,
          class: 'average-1-sigma region'
        },
        {
          axis: 'y',
          end: this.thresholds.lsl,
          class: this.uslLsl ? this.redZone ? 'top-bottom region' : null : null
        },
        {
          axis: 'y',
          start: this.thresholds.usl,
          class: this.uslLsl ? this.redZone ? 'top-bottom region' : null : null
        },
        {
          axis: 'y',
          end: (this.redZone && this.uslLsl) ? null : this.thresholds.lcl,
          class: (this.redZone && this.uslLsl) ? null : 'top-bottom-fill-white region'
        },
        {
          axis: 'y',
          start: (this.redZone && this.uslLsl) ? null : this.thresholds.ucl,
          class: (this.redZone && this.uslLsl) ? null : 'top-bottom-fill-white region'
        },
        {
          axis: 'y',
          end: (this.redZone && this.uslLsl && (this.thresholds.lcl > this.thresholds.lsl) &&
            (this.thresholds.lcl < this.thresholds.usl)) ? this.thresholds.lcl : null,
          start: (this.redZone && this.uslLsl && (this.thresholds.lcl > this.thresholds.lsl) &&
            (this.thresholds.lcl < this.thresholds.usl)) ? this.thresholds.lsl : null,
          class: (this.redZone && this.uslLsl && (this.thresholds.lcl > this.thresholds.lsl) &&
            (this.thresholds.lcl < this.thresholds.usl)) ? 'top-bottom-fill-white region' : null,
        },
        {
          axis: 'y',
          end: (this.redZone && this.uslLsl && (this.thresholds.ucl < this.thresholds.usl) &&
            (this.thresholds.ucl > this.thresholds.lsl)) ? this.thresholds.usl : null,
          start: (this.redZone && this.uslLsl && (this.thresholds.ucl < this.thresholds.usl) &&
            (this.thresholds.ucl > this.thresholds.lsl)) ? this.thresholds.ucl : null,
          class: (this.redZone && this.uslLsl && (this.thresholds.ucl < this.thresholds.usl) &&
            (this.thresholds.ucl > this.thresholds.lsl)) ? 'top-bottom-fill-white region' : null,
        },
        {
          axis: 'y',
          end: (this.redZone && this.uslLsl && (this.thresholds.ucl < this.thresholds.lsl) &&
            (this.thresholds.lcl < this.thresholds.lsl)) ? this.thresholds.usl : null,
          start: (this.redZone && this.uslLsl && (this.thresholds.ucl < this.thresholds.lsl) &&
            (this.thresholds.lcl < this.thresholds.lsl)) ? this.thresholds.lsl : null,
          class: (this.redZone && this.uslLsl && (this.thresholds.ucl < this.thresholds.lsl) &&
            (this.thresholds.lcl < this.thresholds.lsl)) ? 'top-bottom-fill-white region' : null,
        },
      ],
      zoom: {
        type: 'scroll',
        enabled: true
      },
      grid: {
        lines: {
          front: false
        }
      },
      tooltip: {
        grouped: false,
        format: {
          title: (d: any) => this.customToolTip(d)
        }
      }
    });

    this.graphUtil.addThresholdLines(this.chart, this.uslLsl, this.thresholds);
  }

  colorGenerator(d: any, i: any) {
    let color = this.colors.normal;
    if (i.index || i.index === 0) {
      if (this.violatedIndexes.rule7.indexOf(i.index) !== -1 && this.rule7Enabled) {
        color = this.colors.rule7Violation;
      }
      if (this.violatedIndexes.rule6.indexOf(i.index) !== -1 && this.rule6Enabled) {
        color = this.colors.rule6Violation;
      }
      if (this.violatedIndexes.rule1.indexOf(i.index) !== -1 && this.rule1Enabled) {
        color = this.colors.rule1Violation;
      }
    }
    if (this.outliers) {
      color = this.colors.normal;
    }
    // white dots
    if ((i.value < this.thresholds.lsl || i.value > this.thresholds.usl) &&
      this.uslLsl && this.redZone) {
      color = this.colors.outsideLslUsl;
    }
    return color;
  }

  onPointSelect(point) {
    if (point && (point.index || point.index === 0)) {
      const newPoint = this.data[point.index];
      newPoint.indexInData = point.index;

      this.selectedPoints.push(newPoint);
      this.pointSelection.emit(this.selectedPoints);
    }
  }

  onPointUnSelect(point) {
    if (point && (point.index || point.index === 0)) {
      this.selectedPoints = this.selectedPoints.filter((eachPt) => eachPt.indexInData !== point.index);
      this.pointSelection.emit(this.selectedPoints);
    }
  }

  customToolTip(d: any) {
    return this.datePipe.transform(d, 'MMM d, HH:mm');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
